import type { TransactionDto, TransactionTableEntities } from '../common/dtos/transactions/transaction-dto';
import type { PaymentFormData } from '../common/interfaces/payment-form-data';
import { PaymentStatus } from '../common/enums/payment-status';
import { getSearchParams, type SearchDto } from '../utils/append-url-param';
import { type ExportTransactionsReportParams, ReportType } from '../common/models/export/export.model';
import type { BaseDataTableUrlParamsModel } from '../common/models/base/base-data-table-url-params.model';

interface TransactionsStoreState {
  filter: SearchDto;
  transactionsList: TransactionTableEntities;
  isLoading: boolean;
  transactionsRequestId: number;
  currentPayment: TransactionDto | null;
  isLoadingPayment: boolean;
}

export interface FetchTransactionsListParams extends BaseDataTableUrlParamsModel<TransactionDto> {}

export const useTransactionsStore = defineStore('transactionsStore', {
  state: (): TransactionsStoreState => ({
    filter: {
      search: '',
      skip: 0,
      take: 10,
      sortOrder: {
        columnName: 'createdAt',
        order: 'descend',
      },
      from: void 0,
      to: void 0,
    },
    transactionsList: {
      data: [],
      total: 0,
    },
    isLoading: false,
    transactionsRequestId: 0,
    currentPayment: null,
    currentPaymentId: null,
    isLoadingPayment: false,
  }),
  actions: {
    updateFilter(filter: SearchDto) {
      this.filter = {
        ...this.filter,
        ...filter,
      };
    },
    async fetchFilteredTransactions() {
      const { $snapApi } = useNuxtApp();
      const requestId = ++this.transactionsRequestId;
      this.isLoading = true;

      const urlParams = getSearchParams({
        search: this.filter.search ?? '',
        skip: this.filter.skip ?? 0,
        take: this.filter.take ?? 10,
        sortOrder: {
          columnName: this.filter.sortOrder?.columnName ?? 'createdAt',
          order: this.filter.sortOrder?.order ?? 'descend',
        },
        from: this.filter.from,
        to: this.filter.to,
      });

      try {
        const response = await $snapApi({
          method: 'GET',
          url: `merchant/transactions`,
          params: urlParams,
        });

        if (requestId === this.transactionsRequestId && response.status === 200) {
          this.transactionsList = {
            data: response.data.list,
            total: response.data.count,
          };
        }
      } finally {
        if (requestId === this.transactionsRequestId) {
          this.isLoading = false;
        }
      }
    },
    async downloadAttachmnetsForPayment(paymentId: number) {
      const { $snapApi } = useNuxtApp();
      const response = await $snapApi({
        method: 'GET',
        url: `payment/${paymentId}/attachment`,
        responseType: 'blob',
      });

      if (response && response.status === 200) {
        const contentDisposition = response.headers['content-disposition'];
        const filenameMatch = contentDisposition?.match(/filename="(.+?)"/);
        const filename = filenameMatch ? filenameMatch[1] : `attachments_${Date.now()}.zip`;

        triggerDownloadFile(response, filename);
      } else {
        console.error('Failed to download attachments:', response?.status);
      }
    },
    async cancelPayment(paymentId: number | null): Promise<boolean> {
      const { $snapApi, $toastSuccess, $toastError } = useNuxtApp();
      const data = {
        paymentId,
        status: 'Cancelled',
      };

      const response = await $snapApi({
        method: 'POST',
        url: 'cancel-payment',
        data,
      });

      if (response && response.status === 200) {
        $toastSuccess('❌ Payment cancelled');
        if (paymentId != null) {
          await this.refreshTransactionsAndDetail(paymentId);
        } else {
          await this.fetchFilteredTransactions();
        }

        return true;
      }

      $toastError('We were unable to cancel that payment. You can retry');

      return false;
    },
    async requestPaperCheckCancellation(paymentId: number): Promise<boolean> {
      const { $snapApi, $toastError } = useNuxtApp();
      const response = await $snapApi({
        method: 'POST',
        url: 'request-paper-check-cancellation',
        data: { paymentId },
      });

      if (response && response.status === 200) {
        // The open payment shows this request inline, so a toast would repeat it.
        await this.fetchPaymentById(paymentId, { clearWhileLoading: false });
        await this.fetchFilteredTransactions();
        if (this.currentPayment?.id === paymentId) {
          this.patchTransactionInList(paymentId, this.currentPayment);
        }

        return true;
      }

      $toastError('We were unable to request cancellation for that check. You can retry');

      return false;
    },
    async refreshTransactionsAndDetail(paymentId: number): Promise<void> {
      const hasOpenDetail = this.currentPaymentId === paymentId || this.currentPayment?.id === paymentId;

      if (hasOpenDetail) {
        await this.fetchPaymentById(paymentId, { clearWhileLoading: false });
      }

      await this.fetchFilteredTransactions();

      if (hasOpenDetail && this.currentPayment?.id === paymentId) {
        this.patchTransactionInList(paymentId, this.currentPayment);
      } else if (!hasOpenDetail) {
        this.patchTransactionStatusInList(paymentId, PaymentStatus.Cancelled);
      }
    },
    async resendPayment(paymentId: number | undefined, resendFormData: PaymentFormData) {
      const { $snapApi, $toastError, $fireConfetti, $toastSuccess } = useNuxtApp();
      let data;
      if (resendFormData.eobFile) {
        data = new FormData();
        data.append('file', resendFormData.eobFile);
        data.append('paymentId', String(paymentId));
        data.append('status', 'Resend');
        data.append('email', resendFormData.email);
        data.append('amount', resendFormData.amount);
        data.append('memo', String(resendFormData.memo));
      } else {
        data = {
          paymentId: paymentId,
          status: 'Resend',
          email: resendFormData.email,
          amount: resendFormData.amount,
          memo: resendFormData.memo,
        };
      }

      const response = await $snapApi({
        method: 'POST',
        url: '/cancel-payment',
        data,
      });

      if (response && response.status === 200) {
        $fireConfetti();
        $toastSuccess('➡️ Payment sent');
        await this.fetchFilteredTransactions();
      } else {
        $toastError('We had some trouble resending that payment. You can retry');
      }
    },
    async getTransactionsExportReportParams(
      reportType: ReportType,
      options?: FetchTransactionsListParams,
    ): Promise<ExportTransactionsReportParams> {
      const baseParams = {
        type: reportType,
        options: options,
        successMessage: `${reportType.toUpperCase()} report successfully downloaded!`,
        errorMessage: `Failed to load a ${reportType.toUpperCase()} report!`,
      };

      const url = 'export/report';

      const reportSettings = {
        [ReportType.CSV]: {
          urlPath: 'transactions-csv',
          filename: 'transactions-report.csv',
        },
        [ReportType.EXCEL]: {
          urlPath: 'transactions-excel',
          filename: 'transactions-report.xlsx',
        },
        [ReportType.PDF]: {
          urlPath: 'transactions-pdf',
          filename: 'transactions-report.pdf',
        },
      };

      const { urlPath, filename } = reportSettings[reportType];

      const fullUrl = options ? appendUrlDataTableParamsT(`${url}/${urlPath}`, options) : `${url}/${urlPath}`;

      const fetchReportParams: ExportTransactionsReportParams = {
        ...baseParams,
        url: fullUrl,
        filename: filename,
      };

      return fetchReportParams;
    },
    async fetchReport(params: ExportTransactionsReportParams): Promise<void> {
      const { $snapApi, $toastError, $toastSuccess } = useNuxtApp();

      try {
        const response = await $snapApi({
          method: 'GET',
          url: params.url,
          responseType: 'blob',
        });

        triggerDownloadFile(response, params.filename);

        $toastSuccess(params.successMessage);
      } catch (error) {
        $toastError(params.errorMessage);
      } finally {
      }
    },
    async fetchPaymentById(paymentId: number, options?: { clearWhileLoading?: boolean }): Promise<void> {
      const { $snapApi } = useNuxtApp();
      const clearWhileLoading = options?.clearWhileLoading ?? true;
      this.isLoadingPayment = true;
      if (clearWhileLoading) {
        this.currentPayment = null;
      }
      this.currentPaymentId = paymentId;

      try {
        const response = await $snapApi({
          method: 'GET',
          url: `merchant/transactions/${paymentId}`,
        });

        if (response.status === 200) {
          this.currentPayment = response.data;
        }
      } finally {
        this.isLoadingPayment = false;
      }
    },
    clearCurrentPayment() {
      this.currentPayment = null;
      this.currentPaymentId = null;
    },
    patchTransactionStatusInList(paymentId: number, status: string) {
      this.patchTransactionInList(paymentId, { status });
    },
    patchTransactionInList(paymentId: number, patch: Partial<TransactionDto>) {
      const index = this.transactionsList.data.findIndex((row) => row.id === paymentId);
      if (index === -1) {
        return;
      }
      const nextData = this.transactionsList.data.map((row) => (row.id === paymentId ? { ...row, ...patch } : row));
      this.transactionsList = {
        data: nextData,
        total: this.transactionsList.total,
      };
    },
    async fetchCheckPdf(paymentId: number): Promise<string | null> {
      const { $snapApi } = useNuxtApp();

      try {
        const response = await $snapApi({
          method: 'GET',
          url: `checkflo/check-pdf/${paymentId}`,
          responseType: 'blob',
        });

        if (response.status === 200) {
          return URL.createObjectURL(response.data);
        }
      } catch {
        return null;
      }

      return null;
    },
    async fetchPaymentAttachment(paymentId: number, attachmentId: number) {
      const { $snapApi } = useNuxtApp();

      try {
        const response = await $snapApi({
          method: 'GET',
          url: `payment/${paymentId}/attachments/${attachmentId}`,
          responseType: 'blob',
        });

        if (response?.status === 200) {
          return response;
        }
      } catch {
        return null;
      }

      return null;
    },
    async updateAvailablePaymentMethods(paymentId: number, availablePaymentMethods: string[]) {
      const { $snapApi } = useNuxtApp();
      this.currentPaymentId = paymentId;
      const response = await $snapApi({
        method: 'PUT',
        url: 'payment/available-payment-methods',
        data: { paymentId, availablePaymentMethods },
      });

      if (response.status === 200 && this.currentPayment?.id === paymentId) {
        this.currentPayment = { ...this.currentPayment, availablePaymentMethods };
      }

      return response;
    },
  },
});
